/**
 * Users Dual-Write Test Suite
 *
 * This utility tests user updates in Firestore and verifies
 * that Supabase and Firestore are in sync.
 *
 * NOTE: This test suite does NOT create new users in auth.
 * It only updates existing user records in the database.
 *
 * Usage:
 *   import { runUsersDualWriteTests } from './utils/usersDualWriteTest';
 *   await runUsersDualWriteTests(db, testConfig);
 */

import { sanitizeForFirestore } from '../global/Firebase/productSanitize'
import { updateUser, updateUserStore, rootDb } from '../global/Firebase/firebase';
import { api } from '../global/API/apiClient';
import { getDefaultStore } from 'jotai';
import {
  organizationAtom,
  userIdAtom,
  storesDictAtom,
} from '../global/AtomsProvider';

// Test result tracking
const testResults = {
  passed: [],
  failed: [],
  skipped: [],
};

/**
 * All test variations to run
 */
export const USER_TEST_VARIATIONS = [
  // Basic updates (U1-U2)
  { id: 'U1', category: 'basic', name: 'Update user name' },
  { id: 'U2', category: 'basic', name: 'Update user role' },

  // Store update (U3)
  { id: 'U3', category: 'store', name: 'Update user store' },
];

/**
 * Generate test update data for a specific variation
 */
export function generateTestUpdate(variation, testData) {
  const timestamp = Date.now();

  switch (variation.id) {
    case 'U1': // Update name
      return {
        name: `Test User Name ${timestamp}`,
      };

    case 'U2': // Update role
      return {
        role: 'manager', // Toggle between roles
      };

    case 'U3': // Update store
      return {
        store: testData.targetStore,
        _isStoreUpdate: true,
      };

    default:
      return {};
  }
}

/**
 * Run a single test variation
 */
async function runSingleTest(variation, testData, options = {}) {
  const { delayMs = 5000 } = options;

  console.log(`\n--- Running test ${variation.id}: ${variation.name} ---`);

  try {
    // Get the current user's document reference from ROOT db (users are at root level)
    const userRef = rootDb.collection('users').doc(testData.userId);

    // Get original user data
    const originalDoc = await userRef.get();
    if (!originalDoc.exists) {
      throw new Error(`User document not found: ${testData.userId}`);
    }
    const originalData = originalDoc.data();

    // Generate test update
    const updateData = generateTestUpdate(variation, testData);

    // Check if this is a store update (uses different function)
    const isStoreUpdate = updateData._isStoreUpdate;
    delete updateData._isStoreUpdate;

    console.log(`Updating user with:`, JSON.stringify(sanitizeForFirestore(updateData), null, 2));

    // Perform the update via frontend code (triggers dual-write)
    if (isStoreUpdate) {
      await updateUserStore(userRef, updateData.store);
    } else {
      await updateUser(userRef, updateData);
    }

    // Wait for Supabase write to complete (CRITICAL: 5 second wait)
    console.log(`Waiting ${delayMs}ms for Supabase sync...`);
    await new Promise(resolve => setTimeout(resolve, delayMs));

    // Compare using the API
    const comparisonResult = await compareViaApi(testData.userId, userRef, updateData, isStoreUpdate);

    // Restore original data
    console.log(`Restoring original user data...`);
    if (isStoreUpdate) {
      await updateUserStore(userRef, originalData.store);
    } else {
      const restoreData = {};
      if (updateData.name !== undefined) restoreData.name = originalData.name;
      if (updateData.role !== undefined) restoreData.role = originalData.role;
      if (Object.keys(restoreData).length > 0) {
        await updateUser(userRef, restoreData);
      }
    }

    // Log result
    if (comparisonResult.matches) {
      console.log(`PASS: ${variation.name}`);
      testResults.passed.push({
        variation,
        result: comparisonResult,
      });
    } else {
      console.log(`FAIL: ${variation.name}`);
      console.log('Differences:', JSON.stringify(sanitizeForFirestore(comparisonResult.differences), null, 2));
      testResults.failed.push({
        variation,
        result: comparisonResult,
      });
    }

    return comparisonResult;
  } catch (error) {
    console.error(`ERROR in test ${variation.id}:`, error.message);
    console.error(`Stack trace:`, error.stack);
    testResults.failed.push({
      variation,
      error: error.message,
      stack: error.stack,
    });
    return null;
  }
}

/**
 * Compare user data via backend API
 */
async function compareViaApi(userId, userRef, updateData, isStoreUpdate) {
  try {
    const response = await api.get(`/users/compare/${userId}`);
    const apiResult = response.data;

    // Convert API result to our comparison format
    if (!apiResult.exists) {
      return {
        userId,
        foundInSupabase: false,
        foundInFirestore: true,
        matches: false,
        differences: [{
          field: 'user',
          supabaseValue: null,
          firestoreValue: 'exists',
          severity: 'critical',
        }],
        warnings: [],
      };
    }

    // Get Firestore data for comparison
    let firestoreData = null;
    try {
      const doc = await userRef.get();
      if (doc.exists) {
        firestoreData = { id: doc.id, ...doc.data() };
      }
    } catch (error) {
      console.warn(`Could not fetch from Firestore: ${error.message}`);
    }

    const supabaseData = apiResult.supabaseData;
    const differences = [];

    // Compare updated fields
    if (isStoreUpdate) {
      // Compare store - need to resolve Firestore ID to Supabase UUID
      const supabaseStoreId = supabaseData?.store_id;
      const firestoreStoreId = firestoreData?.store?.id || firestoreData?.store;

      // Look up the Supabase UUID for the Firestore store ID
      let resolvedSupabaseStoreId = null;
      if (firestoreStoreId) {
        try {
          const storeCompare = await api.get(`/stores/compare/${firestoreStoreId}`);
          if (storeCompare.data?.exists && storeCompare.data?.supabaseData?.id) {
            resolvedSupabaseStoreId = storeCompare.data.supabaseData.id;
          }
        } catch (e) {
          console.warn('Could not resolve store ID:', e.message);
        }
      }

      // Compare the resolved UUIDs (both should be Supabase UUIDs now)
      if (!valuesMatch(supabaseStoreId, resolvedSupabaseStoreId)) {
        differences.push({
          field: 'store_id',
          supabaseValue: supabaseStoreId,
          firestoreValue: `${firestoreStoreId} (resolved: ${resolvedSupabaseStoreId})`,
          severity: 'critical',
        });
      }
    } else {
      // Compare regular fields
      if (updateData.name !== undefined) {
        const supabaseValue = normalizeValue(supabaseData?.name);
        const firestoreValue = normalizeValue(firestoreData?.name);
        if (!valuesMatch(supabaseValue, firestoreValue)) {
          differences.push({
            field: 'name',
            supabaseValue,
            firestoreValue,
            severity: 'critical',
          });
        }
      }

      if (updateData.role !== undefined) {
        const supabaseValue = normalizeValue(supabaseData?.role);
        // Firestore role can be an object with id or a string
        const firestoreRole = firestoreData?.role;
        const firestoreValue = normalizeValue(
          typeof firestoreRole === 'object' ? firestoreRole?.id : firestoreRole
        );
        if (!valuesMatch(supabaseValue, firestoreValue)) {
          differences.push({
            field: 'role',
            supabaseValue,
            firestoreValue,
            severity: 'critical',
          });
        }
      }
    }

    return {
      userId,
      foundInSupabase: true,
      foundInFirestore: !!firestoreData,
      matches: differences.length === 0,
      differences,
      warnings: [],
      supabaseData,
      firestoreData,
    };
  } catch (error) {
    console.warn('API comparison failed:', error.message);
    return {
      userId,
      foundInSupabase: false,
      foundInFirestore: true,
      matches: false,
      differences: [{
        field: 'api_error',
        supabaseValue: error.message,
        firestoreValue: 'exists',
        severity: 'critical',
      }],
      warnings: [error.message],
    };
  }
}

function normalizeValue(value) {
  if (value === null || value === undefined) return null;
  if (value === '') return null;
  if (typeof value === 'number') return Number(value);
  if (typeof value === 'boolean') return value;
  return value;
}

function valuesMatch(a, b) {
  if ((a === null || a === undefined) && (b === null || b === undefined)) {
    return true;
  }
  if (a === null || a === undefined || b === null || b === undefined) {
    return false;
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return Math.abs(a - b) < 0.01;
  }
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    return Boolean(a) === Boolean(b);
  }
  return String(a) === String(b);
}

/**
 * Run all test variations
 */
export async function runUsersDualWriteTests(passedDb, options = {}) {
  const {
    variations = USER_TEST_VARIATIONS,
    delayMs = 5000,
    stopOnFailure = false,
  } = options;

  // Reset results
  testResults.passed = [];
  testResults.failed = [];
  testResults.skipped = [];

  // Get test data from Jotai store
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const storesDict = jotaiStore.get(storesDictAtom);
  const userId = jotaiStore.get(userIdAtom);

  // Note: Users are stored at ROOT Firestore level, not under organization
  // We use rootDb directly imported from firebase.ts

  if (!organization || Object.keys(organization).length === 0) {
    throw new Error('Organization not found. Please ensure you are logged in.');
  }

  if (!userId) {
    throw new Error('User not found. Please ensure you are logged in.');
  }

  // Get stores for store update test
  const storeIds = Object.keys(storesDict || {});
  const targetStoreId = storeIds.length > 1 ? storeIds[1] : storeIds[0];
  const targetStore = storesDict?.[targetStoreId];

  const testData = {
    organizationId: organization.id,
    userId,
    stores: storesDict,
    targetStore: targetStore ? { id: targetStoreId, name: targetStore.name } : null,
  };

  console.log('\n========================================');
  console.log('USERS DUAL-WRITE TEST SUITE');
  console.log('========================================');
  console.log(`Organization: ${organization.name || organization.id}`);
  console.log(`Testing User ID: ${userId}`);
  console.log(`Target Store for test: ${targetStore?.name || targetStoreId || 'None'}`);
  console.log(`Variations to test: ${variations.length}`);
  console.log('========================================\n');

  // Run each test
  for (const variation of variations) {
    // Skip store test if no alternative store available
    if (variation.id === 'U3' && storeIds.length < 2) {
      console.log(`SKIP: ${variation.name} - Need at least 2 stores for this test`);
      testResults.skipped.push({ variation, reason: 'Need at least 2 stores' });
      continue;
    }

    const result = await runSingleTest(variation, testData, { delayMs });

    if (stopOnFailure && result && !result.matches) {
      console.log('\nStopping due to failure (stopOnFailure=true)');
      break;
    }
  }

  // Print summary
  console.log('\n========================================');
  console.log('TEST SUMMARY');
  console.log('========================================');
  console.log(`Passed: ${testResults.passed.length}`);
  console.log(`Failed: ${testResults.failed.length}`);
  console.log(`Skipped: ${testResults.skipped.length}`);
  console.log('========================================\n');

  if (testResults.failed.length > 0) {
    console.log('FAILED TESTS:');
    testResults.failed.forEach(({ variation, result, error }) => {
      console.log(`  - ${variation.id}: ${variation.name}`);
      if (error) {
        console.log(`    Error: ${error}`);
      } else if (result?.differences) {
        result.differences.forEach(diff => {
          console.log(`    ${diff.field}: Supabase=${JSON.stringify(sanitizeForFirestore(diff.supabaseValue))} vs Firestore=${JSON.stringify(sanitizeForFirestore(diff.firestoreValue))}`);
        });
      }
    });
  }

  return testResults;
}

/**
 * Quick smoke test - runs only the basic update tests
 */
export async function runQuickUsersSmokeTest(passedDb, options = {}) {
  const quickVariations = USER_TEST_VARIATIONS.filter(v =>
    v.id === 'U1' || v.id === 'U2'
  );

  return runUsersDualWriteTests(passedDb, {
    ...options,
    variations: quickVariations,
  });
}

export default {
  runUsersDualWriteTests,
  runQuickUsersSmokeTest,
  generateTestUpdate,
  USER_TEST_VARIATIONS,
};
